import { welcomeImg } from "#web/assets";
import { SpinnerCentered } from "#web/components/common/SpinnerCentered/SpinnerCentered";
import { OnboardingGuide } from "#web/components/onboarding/OnboardingGuide/OnboardingGuide";
import { BlockingMode } from "#web/components/settings/BlockingMode";
import { CustomerTagExclusion } from "#web/components/settings/CustomerTagExclusion";
import { Duplicates } from "#web/components/settings/Duplicates";
import { EmailAlert } from "#web/components/settings/EmailAlert";
import { ExclusionList } from "#web/components/settings/ExclusionList";
import { ExtensionsSupported } from "#web/components/settings/ExtensionsSupported";
import Section from "#web/components/settings/Section";
import { SubscriptionSection } from "#web/components/settings/Subscription";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { routeConfig } from "#web/configs/route";
import useSafeNavigate from "#web/hooks/useSafeNavigate";
import { api } from "#web/services/api";
import { JSONValue } from "@gadget-client/keepcart-server";
import { useGlobalAction } from "@gadgetinc/react";
import { SaveBar } from "@shopify/app-bridge-react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  InlineStack,
  Link,
  MediaCard,
  Page,
  Tabs,
  Text,
  TextField,
  VideoThumbnail,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface ShopSettings {
  alertEmails?: JSONValue | null;
  alertType?: null | string[];
  blockingReplaceCode?: null | string;
  customerTagsBlocked?: JSONValue | null;
  duplicateBlockingMode?: null | string;
  exclusionCodes?: JSONValue | null;
  extensionBlockingMode?: null | string;
  highRiskCodeCurrencyMinimum?: null | number;
  highRiskCodePercentageMinimum?: null | number;
}

export const Settings = () => {
  const navigate = useSafeNavigate();
  const location = useLocation();

  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [selected, setSelected] = useState(shop?.subscribedTo("blocking") ? 0 : 1);
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  }, []);
  const tabs = [
    {
      content: "Extension Blocking",
      disabled: !shop?.subscribedTo("blocking"),
      id: "extension-blocking",
      panelID: "extension-blocking",
    },
    {
      content: "Coupon Leak Scanner",
      id: "leaks",
      panelID: "leaks",
    },
    {
      content: "Duplicate Blocking",
      disabled: !shop?.subscribedTo("blocking"),
      id: "duplicate-blocking",
      panelID: "duplicate-blocking",
    },
    {
      content: "Alerts",
      id: "alerts",
      panelID: "alerts",
    },
    {
      content: "Billing",
      id: "billing",
      panelID: "billing",
    },
  ];

  // Set the selected tab based on the location state
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (location.state?.tab === "alerts") {
      setSelected(3);
      // Clean up the location.state.tab attribute
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      location.state.tab = undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  }, [location.state?.tab]);

  const [settings, setSettings] = useState<ShopSettings>({});
  const [prevSettings, setPrevSettings] = useState<ShopSettings>({});
  const [loading, setLoading] = useState(false);

  const [, setExtensionBlockingMode] = useGlobalAction(api.setExtensionBlockingMode);
  const [, setBlockingReplaceCode] = useGlobalAction(api.setBlockingReplaceCode);
  const [, setDuplicateBlockingMode] = useGlobalAction(api.setDuplicateBlockingMode);
  const [, setCustomerTagsBlocked] = useGlobalAction(api.setCustomerTagExclusion);
  const [, setExclusionList] = useGlobalAction(api.setExclusionList);

  // For the sauna feature
  const handleChange = useCallback((value: string) => {
    setSettings((prevData) => ({ ...prevData, blockingReplaceCode: value }));
  }, []);

  const handleCheckboxChange = (field: string) => (newValue: boolean) => {
    setSettings((prevData) => {
      const updatedAlertType = newValue
        ? [...(prevData.alertType ?? []), field]
        : (prevData.alertType ?? []).filter((type) => type !== field);
      return { ...prevData, alertType: updatedAlertType };
    });
  };

  // 2. Initialize the settings
  useEffect(() => {
    // 1. Get the discount code
    const settings = {
      alertEmails: shop?.alertEmails,
      alertType: shop?.alertType,
      blockingReplaceCode: shop?.blockingReplaceCode,
      customerTagsBlocked: shop?.customerTagsBlocked,
      duplicateBlockingMode: shop?.duplicateBlockingMode,
      exclusionCodes: shop?.exclusionCodes,
      extensionBlockingMode: shop?.extensionBlockingMode,
      highRiskCodeCurrencyMinimum: shop?.highRiskCodeCurrencyMinimum,
      highRiskCodePercentageMinimum: shop?.highRiskCodePercentageMinimum,
    };

    setSettings({ ...Object.freeze(settings) });
    setPrevSettings({ ...Object.freeze(settings) });
  }, []);

  useEffect(() => {
    async function run() {
      if (!shop) {
        return;
      }

      if (settings.extensionBlockingMode !== prevSettings.extensionBlockingMode)
        await setExtensionBlockingMode({
          extensionBlockingMode: settings.extensionBlockingMode,
          shopId: shop.id,
        });
      if (settings.blockingReplaceCode !== prevSettings.blockingReplaceCode)
        await setBlockingReplaceCode({
          blockingReplaceCode: settings.blockingReplaceCode,
          shopId: shop.id,
        });
      if (settings.duplicateBlockingMode !== prevSettings.duplicateBlockingMode)
        await setDuplicateBlockingMode({
          duplicateBlockingMode: settings.duplicateBlockingMode,
          shopId: shop.id,
        });
      if (settings.customerTagsBlocked !== prevSettings.customerTagsBlocked) {
        await setCustomerTagsBlocked({
          customerTagExclusionList: settings.customerTagsBlocked as string[],
          shopId: shop.id,
        });
      }
      if (settings.exclusionCodes !== prevSettings.exclusionCodes) {
        await setExclusionList({
          exclusionList: settings.exclusionCodes as string[],
          shopId: shop.id,
        });
      }
      if (settings.customerTagsBlocked !== prevSettings.customerTagsBlocked) {
        await setCustomerTagsBlocked({
          customerTagExclusionList: settings.customerTagsBlocked as string[],
          shopId: shop.id,
        });
      }
      if (settings.alertType !== prevSettings.alertType) {
        // @ts-expect-error JSONValue are annoying to work with
        await shop.update({ alertType: settings.alertType });
      }
      if (settings.alertEmails !== prevSettings.alertEmails) {
        await shop.update({ alertEmails: settings.alertEmails });
      }

      // ED - Persistency of new field values in the shop database
      if (settings.highRiskCodePercentageMinimum !== prevSettings.highRiskCodePercentageMinimum) {
        await shop.update({ highRiskCodePercentageMinimum: settings.highRiskCodePercentageMinimum });
      }
      if (settings.highRiskCodeCurrencyMinimum !== prevSettings.highRiskCodeCurrencyMinimum) {
        await shop.update({ highRiskCodeCurrencyMinimum: settings.highRiskCodeCurrencyMinimum });
      }

      setPrevSettings({ ...settings });
      setLoading(false);
      shopify.toast.show("Settings saved");
    }
    if (loading) {
      void run();
    }
  }, [loading]);

  const setEmails = useCallback((emails: JSONValue) => {
    setSettings((prevData) => ({ ...prevData, alertEmails: emails }));
  }, []);

  // not save to gadget, only change the state
  const changeDuplicateBlockingMode = useCallback((mode: string) => {
    setSettings((prevData) => ({ ...prevData, duplicateBlockingMode: mode }));
  }, []);

  const changeExtensionBlockingMode = useCallback(
    (mode: string) => {
      // checkout version is always checkout extensibility if they are not on disabled
      if (mode != "disabled" && shop?.version != "checkout-extensibility") {
        void shop?.update({ version: "checkout-extensibility" });
      }
      setSettings((prevData) => ({ ...prevData, extensionBlockingMode: mode }));
    },
    [shop],
  );

  const setExclusionCodes = useCallback((exclusionCodesList: string[]) => {
    // change the state
    setSettings((prevData) => ({
      ...prevData,
      exclusionCodes: exclusionCodesList,
    }));
  }, []);

  const openDocs = () => {
    window.open("https://keepcart.co/docs");
  };

  const setCustomerTagsBlockedInput = useCallback((tags: string[]) => {
    setSettings((prevData) => ({ ...prevData, customerTagsBlocked: tags }));
  }, []);

  // ED - Storage of new fields in settings list
  const setHighRiskCodePercentageMinimumInput = useCallback((value: number) => {
    setSettings((prevData) => ({
      ...prevData,
      highRiskCodePercentageMinimum: value,
    }));
  }, []);

  const setHighRiskCodeCurrencyMinimumInput = useCallback((value: number) => {
    setSettings((prevData) => ({
      ...prevData,
      highRiskCodeCurrencyMinimum: value,
    }));
  }, []);

  const validateInput = useCallback((value: string) => {
    if (value === "") {
      return value;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return null;
    }

    if (numericValue < 1) {
      return "1";
    } else {
      return parseInt(value);
    }
  }, []);

  const shopify = useAppBridge();

  useEffect(() => {
    if (JSON.stringify(settings) != JSON.stringify(prevSettings)) {
      void shopify.saveBar.show("settings-save-bar");
    } else {
      void shopify.saveBar.hide("settings-save-bar");
    }
  }, [settings, prevSettings]);

  return (
    <Page backAction={{ content: "Dashboard", onAction: () => void navigate(routeConfig["/"]) }} title="Settings">
      {shopError ? (
        <Banner title="Error loading settings" tone="critical">
          <p>Please try again later. If the issue persists, please contact support.</p>
        </Banner>
      ) : shopFetching ? (
        <SpinnerCentered />
      ) : (
        <>
          <SaveBar id="settings-save-bar">
            <button
              loading={loading ? "" : undefined}
              onClick={() => {
                setLoading(true);
              }}
              variant="primary"
            ></button>
            <button
              onClick={() => {
                setSettings(prevSettings);
              }}
            ></button>
          </SaveBar>

          <div style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <Box paddingBlockEnd="600">
              <Card padding="0">
                <Tabs onSelect={handleTabChange} selected={selected} tabs={tabs} />
              </Card>
            </Box>
          </div>

          <BlockStack gap="400">
            {shop?.subscribedTo("blocking") && tabs[selected]!.id === "extension-blocking" && (
              <>
                <Section
                  // @ts-expect-error Type Element is not assignable to type string
                  description={
                    <>
                      Learn how to setup KeepCart <br />
                      <Link target="_blank" url="https://www.keepcart.co/extension-blocking-newcheckout">
                        See instructions here
                      </Link>
                    </>
                  }
                  title="KeepCart Setup"
                >
                  <OnboardingGuide />
                </Section>

                <Divider />

                <Section title="Extension Blocking Options">
                  <Section.Card description="Enable or disable Extension Blocking" title="Extension blocking">
                    <BlockingMode update={changeExtensionBlockingMode} value={settings.extensionBlockingMode ?? ""} />
                  </Section.Card>
                  <Section.Card
                    description="We will block every code an extension attempts to give out, but will give this code to the user at the end of their extension attempt"
                    title="Code for  users"
                  >
                    <TextField
                      autoComplete="off"
                      label=""
                      onChange={handleChange}
                      placeholder="Add a coupon code (optional)"
                      value={settings.blockingReplaceCode ?? ""}
                    />
                  </Section.Card>
                  <Section.Card description="Codes that are not going to be blocked" title="Exclude codes">
                    <ExclusionList exclusionCodes={settings.exclusionCodes as string[]} onChange={setExclusionCodes} />
                  </Section.Card>
                  <Section.Card
                    description="We will only block the codes for the customers that have these tags"
                    title="Only block codes for customers with specific tags"
                  >
                    <CustomerTagExclusion
                      customerTagsBlocked={settings.customerTagsBlocked as string[]}
                      onChange={setCustomerTagsBlockedInput}
                    />
                  </Section.Card>
                </Section>
                <Divider />
                <Section description="We protect against the following extensions" title="Protected Extensions">
                  <ExtensionsSupported />
                </Section>
              </>
            )}

            {tabs[selected]!.id === "leaks" && (
              <Section title="High-Risk Leaks">
                <Section.Card
                  description="Coupon codes greater or equal than these values will be flagged as 'High-Risk Leaks'"
                  title="High-Risk Leaks "
                >
                  <TextField
                    autoComplete="off"
                    label=""
                    max={100}
                    min={1}
                    onChange={(value) => {
                      const validatedValue = validateInput(value);
                      if (validatedValue === null) {
                        return;
                      }
                      setHighRiskCodePercentageMinimumInput(Number(validatedValue));
                    }}
                    prefix="%"
                    type="number"
                    value={
                      settings.highRiskCodePercentageMinimum ? settings.highRiskCodePercentageMinimum.toString() : ""
                    }
                  />
                  <TextField
                    autoComplete="off"
                    label=""
                    max={100}
                    min={1}
                    onChange={(value) => {
                      const validatedValue = validateInput(value);
                      if (validatedValue === null) {
                        return;
                      }
                      setHighRiskCodeCurrencyMinimumInput(Number(validatedValue));
                    }}
                    prefix={shop?.currency}
                    type="number"
                    value={settings.highRiskCodeCurrencyMinimum ? settings.highRiskCodeCurrencyMinimum.toString() : ""}
                  />
                </Section.Card>
              </Section>
            )}

            {shop?.subscribedTo("blocking") && tabs[selected]!.id === "duplicate-blocking" && (
              <Section title="Duplicate Blocking">
                <Section.Card
                  description="Requires the KeepCart checkout extension to be enabled"
                  title="Duplicate blocking rules"
                >
                  <Duplicates update={changeDuplicateBlockingMode} value={settings.duplicateBlockingMode ?? ""} />
                </Section.Card>
              </Section>
            )}

            {tabs[selected]!.id === "alerts" && (
              <Section title="Alerts">
                <Section.Card
                  description="Emails that will be notified when a coupon code was leaked"
                  title="Email alerts"
                >
                  <EmailAlert emails={settings.alertEmails as string[]} setEmails={setEmails} />
                  <Box>
                    <BlockStack>
                      <Text as="h4" variant="headingSm">
                        Alert Type
                      </Text>
                      <Checkbox
                        checked={settings.alertType?.includes("scanned")}
                        label="Coupon website leak alerts"
                        onChange={handleCheckboxChange("scanned")}
                      />
                      <Checkbox
                        checked={settings.alertType?.includes("extension")}
                        disabled={!shop?.subscribedTo("blocking")}
                        label={`Coupon extension leak alerts ${!shop?.subscribedTo("blocking") ? "(Upgrade for extension leak alerts)" : ""}`}
                        onChange={handleCheckboxChange("extension")}
                      />
                    </BlockStack>
                    {shop?.subscribedTo("leaked") && (
                      <Box paddingBlockStart="200">
                        <Banner
                          title="Extension leak alerts are not included in your current plan"
                          tone="warning"
                        ></Banner>
                      </Box>
                    )}
                  </Box>
                </Section.Card>
              </Section>
            )}

            {tabs[selected]!.id === "billing" && (
              <>
                <Section title="Subscription">
                  <Section.Card description="Manage your subscription" title="Subscription">
                    <SubscriptionSection />
                  </Section.Card>
                </Section>

                <Divider />

                <Section title="Help & Support">
                  <MediaCard
                    description="Either write us an email (support@keepcart.co) or book a call with one of our teammates"
                    primaryAction={{
                      content: "Book a call",
                      onAction: () => {
                        window.open("https://zcal.co/i/r7H4VFUJ", "_blank");
                      },
                    }}
                    secondaryAction={{
                      content: "Contact us",
                      onAction: () => {
                        void navigator.clipboard.writeText("support@keepcart.co");
                        void shopify.toast.show("Email address copied to clipboard");
                      },
                    }}
                    title="Talk to one of our teammates"
                  >
                    <VideoThumbnail onClick={openDocs} thumbnailUrl={welcomeImg} />
                  </MediaCard>
                </Section>
              </>
            )}

            <InlineStack align="end">
              <Button
                disabled={JSON.stringify(settings) == JSON.stringify(prevSettings)}
                loading={loading}
                onClick={() => {
                  setLoading(true);
                }}
                variant="primary"
              >
                Save
              </Button>
            </InlineStack>
          </BlockStack>
        </>
      )}
    </Page>
  );
};
